/**
 * Web Storage access that never throws.
 *
 * Browser storage is not always there, and its absence must never take a page
 * down. Seen on 29/09/2026 through Sentry: a learner from the LinkedIn campaign
 * opened a course in LinkedIn's in-app browser, where `window.localStorage` is
 * `null`, and `useInvitation` read it during render — the course page never
 * rendered. The same crash happens when cookies and site data are blocked
 * (privacy settings, hospital and corporate PCs with managed policies): there
 * merely touching `window.localStorage` throws a SecurityError. `setItem` can
 * also throw when the quota is exceeded.
 *
 * Everything stored through here is a convenience (a dismissed dialog, a tour
 * already seen, a remembered filter), so when storage is unavailable reads
 * behave as if nothing were stored and writes are dropped.
 */

type StorageArea = 'local' | 'session';

const resolve = (area: StorageArea): Storage | null => {
  try {
    const storage =
      area === 'local' ? window.localStorage : window.sessionStorage;
    return storage ?? null;
  } catch {
    // SecurityError: storage blocked for this origin.
    return null;
  }
};

const makeSafeStorage = (area: StorageArea) => ({
  /** The stored value, or null when missing or when storage is unavailable. */
  getItem(key: string): string | null {
    try {
      return resolve(area)?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },

  /** Whether the value was stored. */
  setItem(key: string, value: string): boolean {
    try {
      const storage = resolve(area);
      if (!storage) return false;
      storage.setItem(key, value);
      return true;
    } catch {
      // QuotaExceededError, or storage revoked while the page was open.
      return false;
    }
  },

  removeItem(key: string): void {
    try {
      resolve(area)?.removeItem(key);
    } catch {
      // Nothing to do: the value could not have been stored either.
    }
  },
});

export const safeLocalStorage = makeSafeStorage('local');
export const safeSessionStorage = makeSafeStorage('session');
